import { useState, useEffect, useCallback } from 'react';
import { getEventCapacity, subscribeToCapacity } from '../services/ticketService';

/**
 * Hook to manage event capacity state with real-time updates
 */
export function useEventCapacity(eventSlug) {
  const [currentCount, setCurrentCount] = useState(0);
  const [maxCapacity, setMaxCapacity] = useState(null);
  const [isAtCapacity, setIsAtCapacity] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCapacity = useCallback(async () => {
    if (!eventSlug) return;

    try {
      const capacity = await getEventCapacity(eventSlug);
      if (capacity) {
        setCurrentCount(capacity.currentCount);
        setMaxCapacity(capacity.maxCapacity);
        setIsAtCapacity(capacity.isAtCapacity);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch capacity:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [eventSlug]);

  useEffect(() => {
    fetchCapacity();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToCapacity(eventSlug, (capacity) => {
      setCurrentCount(capacity.currentCount);
      setMaxCapacity(capacity.maxCapacity);
      setIsAtCapacity(capacity.isAtCapacity);
    });

    return () => {
      unsubscribe();
    };
  }, [eventSlug, fetchCapacity]);

  return {
    currentCount,
    maxCapacity,
    isAtCapacity,
    loading,
    error,
    refresh: fetchCapacity,
  };
}
